import React from "react";
import clsx from "clsx";

import useMediaQuery from "@material-ui/core/useMediaQuery";
import useTheme from "@material-ui/core/styles/useTheme";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useSetListHorsesQueryData } from "hooks/api";
import Link from "components/Link";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import HorseActionsMenu from "pages/HorseListPage/HorseActionsMenu";
import { ICell, SortByFields } from "../HorseList";

import useStyles from "./styles";

interface IProps {
  horse: racehorse360.IHorse;
  getCells: (horse: racehorse360.IHorse) => ICell[];
  className?: string;
  horseDetailPageDataColumn: racehorse360.HorseDetailPageDataColumn;
}

const HorseListRow = (props: IProps) => {
  const { horse, className, getCells, horseDetailPageDataColumn } = props;

  const cells = getCells(horse);
  const additionalCell = horseDetailPageDataColumn
    ? cells.find(c => String(c.type) === String(horseDetailPageDataColumn))
    : null;

  const vetListHorsesRequestKey = useAppSelector(
    state => state.vetHorseListPage.vetListHorsesRequestKey
  );

  const classes = useStyles();
  const { updateListHorses } = useSetListHorsesQueryData();
  const { currentUser } = useLoggedInUser();
  const isRegulatoryVet = currentUser.isRegulatoryVet;

  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"), {
    noSsr: true
  });
  const matchesDownSm = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });

  const shouldShowAdditionalCell = [matchesDownSm, additionalCell].every(
    Boolean
  );

  const updateHorseListData = (horsePatch: racehorse360.IHorse) => {
    updateListHorses([horse.id], horsePatch, vetListHorsesRequestKey);
  };

  const renderCells = cells.map(cell => (
    <div
      key={horse.id + cell.id}
      role="cell"
      className={clsx(classes.tableCellLink, cell.additionalClassName, {
        [classes.hideOnTablet]: cell.desktopOnly
      })}
    >
      {cell.id === SortByFields.LAST_WORK_DATE
        ? cell.render(isXlScreen)
        : cell.render()}
    </div>
  ));

  const renderAdditionalCell = () => {
    if (!shouldShowAdditionalCell) return;

    return (
      <div
        role="cell"
        key={horse.id + additionalCell.id}
        className={clsx(
          classes.tableCellLink,
          additionalCell.additionalClassName
        )}
      >
        {additionalCell.id === SortByFields.LAST_WORK_DATE
          ? additionalCell.render(isXlScreen)
          : additionalCell.render()}
      </div>
    );
  };

  return (
    <div
      key={horse.id}
      className={clsx(classes.rowWrapper, className, {
        regulatoryVetView: isRegulatoryVet
      })}
    >
      {!isRegulatoryVet && (
        <HorseActionsMenu horse={horse} refreshData={updateHorseListData} />
      )}
      <Link
        to={routes.horseDetails.path.replace(":horseId", horse.id)}
        className={clsx(classes.tableBodyRow, {
          [classes.customizedForMobile]: Boolean(horseDetailPageDataColumn),
          regulatoryVetView: isRegulatoryVet
        })}
        dataTestLabel={"vetHorseListItem"}
      >
        {renderCells}
        {renderAdditionalCell()}
      </Link>
    </div>
  );
};

export default React.memo(HorseListRow);
